/**
 * Origin verification helpers used by both ends of the popup transport.
 * Spec §5.3.
 */

/**
 * Schemes a host origin may carry. `https:` is the production case;
 * `chrome-extension:` is a supported host surface too (a wallet embedded from
 * an extension page — CEF-AI/wallet#115); `http:` is here so test setups and
 * `localhost:port` work. Callers can layer tighter rules (e.g. "only https in
 * production") on top.
 *
 * This constant is DECLARATIVE, not a gate. Nothing in this package reads it —
 * `isMatchingOrigin`, the check every transport actually routes through, never
 * consults it, and adding a scheme here neither allows nor blocks a message.
 * It exists so a downstream consumer filtering host origins can agree with the
 * SDK about what a supported host surface is. Do not mistake it for
 * enforcement, and do not add a scheme here expecting it to take effect.
 */
export const ALLOWED_ORIGIN_SCHEMES: readonly string[] = ['https:', 'http:', 'chrome-extension:'];

/**
 * Strict origin equality check. Returns true iff both inputs are non-empty,
 * URL-parseable, and have matching `origin` (scheme + host + port).
 *
 * HAZARD, for whoever tests this: this compares `new URL(x).origin`, and under
 * Node/whatwg-url — so happy-dom and jsdom — `chrome-extension:` is not a
 * registered scheme, so EVERY `chrome-extension://<id>` URL has origin `"null"`
 * and any two extensions compare EQUAL. Real Chrome registers the scheme and
 * returns the tuple origin, so production is correct; but a unit test asserting
 * "a message from a different extension is dropped" passes whether or not this
 * check works. Assert that in a real browser (e2e) or not at all. This applies
 * to every caller — `PopupHostBridge`, `PopupTransport`, `IframeTransport`, and
 * anyone downstream, since this function is exported from the package root.
 */
export function isMatchingOrigin(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    return ua.origin === ub.origin;
  } catch {
    return false;
  }
}

/**
 * Source binding: does this message come from the window this transport owns?
 *
 * This is NOT an authentication boundary — `event.origin` already is one, and
 * the browser sets it from the sending document, so it cannot be forged. This
 * check disambiguates OUR OWN same-origin surfaces from each other, which the
 * origin check cannot do because they share an origin by construction.
 *
 * It matters because a host window can have several wallet surfaces alive at
 * once: `register()`, and the login / cold-sign popup fallbacks taken when the
 * framed ceremony is refused by its effective Permissions Policy, each open a
 * `PopupTransport` while the `IframeTransport` is still listening on the same
 * `hostWindow`. Without this, the popup's `wallet:ready` flips the iframe
 * transport's ready latch and flushes its outbox into an iframe that has not
 * loaded (messages lost, request times out), and the popup's
 * out-of-band messages are attributed to the wrong surface entirely.
 *
 * The original example was `wallet:session:keyed`: a popup-sourced ack was
 * accepted as if the IFRAME had adopted the session, so `_hasKeys` went `true`
 * while the iframe held no keys and the next sign went to a surface that could
 * not perform it. S1 PR2 deleted that message, and the check is no less
 * load-bearing for it — `wallet:diag` is out-of-band on the same terms, and
 * every message correlated by request id still depends on this binding to
 * decide whose reply it is.
 *
 * An absent `expected` (no window yet, or already closed) and an absent
 * `event.source` are both treated as unverifiable and allowed through:
 * synthetic `MessageEvent`s carry no source, and a message that reached here at
 * all already passed the origin check. Only a positive mismatch is rejected.
 */
export function isFromExpectedSource(source: unknown, expected: unknown): boolean {
  if (!source || !expected) return true;
  return source === expected;
}
